import { useState } from 'react';
import { Trophy, Medal, Award, Calendar, CheckCircle2, XCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

interface MonthlyPick {
  id: string;
  productId: string;
  productName: string;
  productSlug: string;
  price: number;
  images: Array<{ url: string; alt: string }>;
  brand: string;
  description: string;
  rank: number;
  title: string;
  summary: string;
  reason: string;
  pros: string[];
  cons: string[];
  targetAudience: string;
  priceComment: string;
  purchaseLinks: Array<{ store: string; url: string; price: number }>;
}

interface MonthlyPicksResponse {
  year: number;
  month: number;
  categoryId: string;
  categoryName: string;
  picks: MonthlyPick[];
}

interface AllMonthlyPicksResponse {
  year: number;
  month: number;
  categories: Array<{
    id: string;
    name: string;
    picks: MonthlyPick[];
  }>;
}

export function MonthlyPicksPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { data: allData, isLoading: loadingAll } = useQuery({
    queryKey: ['monthly-picks'],
    queryFn: async () => {
      const res = await fetch('/api/monthly-picks');
      if (!res.ok) throw new Error('Failed to fetch monthly picks');
      return res.json() as Promise<AllMonthlyPicksResponse>;
    },
  });

  const { data: categoryData, isLoading: loadingCategory } = useQuery({
    queryKey: ['monthly-picks', selectedCategory],
    queryFn: async () => {
      if (!selectedCategory) return null;
      const res = await fetch(`/api/monthly-picks/${selectedCategory}`);
      if (!res.ok) throw new Error('Failed to fetch category picks');
      return res.json() as Promise<MonthlyPicksResponse>;
    },
    enabled: !!selectedCategory,
  });

  const picks = selectedCategory ? categoryData?.picks : allData?.categories.flatMap(c => c.picks) || [];

  if (loadingAll || loadingCategory) {
    return (
      <div className="container py-12 flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="text-gray-600">로딩 중...</p>
      </div>
    );
  }

  const year = allData?.year || new Date().getFullYear();
  const month = allData?.month || new Date().getMonth() + 1;

  return (
    <div className="container py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
          <Calendar size={18} />
          <span>{year}년 {month}월</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">이달의 추천</h1>
        <p className="text-gray-600">
          전문가가 엄선한 이달의 베스트 제품을 만나보세요
        </p>
      </div>

      {/* Category Filter */}
      {allData?.categories && (
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === null
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            전체
          </button>
          {allData.categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Picks */}
      <div className="space-y-12">
        {picks?.map((pick) => (
          <div key={pick.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Rank Badge */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
              <div className="flex items-center gap-3">
                {pick.rank === 1 && <Trophy className="w-8 h-8 text-yellow-300" />}
                {pick.rank === 2 && <Medal className="w-8 h-8 text-gray-300" />}
                {pick.rank === 3 && <Award className="w-8 h-8 text-amber-600" />}
                {pick.rank > 3 && <span className="text-2xl font-bold text-white">#{pick.rank}</span>}
                <div>
                  <h2 className="text-xl font-bold text-white">{pick.title}</h2>
                  <p className="text-blue-100 text-sm">{pick.summary}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Product Info */}
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                {/* Product Image */}
                {pick.images && pick.images[0] && (
                  <div className="w-full md:w-48 h-48 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={pick.images[0].url}
                      alt={pick.images[0].alt || pick.productName}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}

                {/* Product Details */}
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-1">{pick.brand}</p>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{pick.productName}</h3>
                  {pick.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4">{pick.description}</p>
                  )}

                  {/* Price */}
                  {pick.price > 0 && (
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-2xl font-bold text-blue-600">
                        {pick.price.toLocaleString()}
                      </span>
                      <span className="text-sm text-gray-600">원</span>
                    </div>
                  )}

                  {/* Purchase Link */}
                  {pick.purchaseLinks && pick.purchaseLinks[0] && (
                    <a
                      href={pick.purchaseLinks[0].url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      쿠팡에서 구매하기 →
                    </a>
                  )}
                </div>
              </div>

              {/* Reason */}
              <div className="bg-blue-50 rounded-xl p-5 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span>🎯</span>
                  <span>추천 이유</span>
                </h4>
                <p className="text-gray-700 whitespace-pre-line">{pick.reason}</p>
              </div>

              {/* Pros & Cons */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Pros */}
                {pick.pros && pick.pros.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <CheckCircle2 size={18} />
                      <span>장점</span>
                    </h4>
                    <ul className="space-y-2">
                      {pick.pros.map((pro, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-green-500 mt-0.5">✓</span>
                          <span>{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Cons */}
                {pick.cons && pick.cons.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                      <XCircle size={18} />
                      <span>단점</span>
                    </h4>
                    <ul className="space-y-2">
                      {pick.cons.map((con, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-red-500 mt-0.5">!</span>
                          <span>{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Target Audience */}
              {pick.targetAudience && (
                <div className="bg-gray-50 rounded-xl p-5 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">👤 누구에게 추천?</h4>
                  <p className="text-gray-700">{pick.targetAudience}</p>
                </div>
              )}

              {/* Price Comment */}
              {pick.priceComment && (
                <div className="text-sm text-gray-500">
                  💰 가격대: {pick.priceComment}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Affiliate Disclosure */}
      <div className="mt-12 text-center text-xs text-gray-400">
        쿠팡 파트너스 활동의 일환으로 수수료를 제공받을 수 있습니다.
      </div>
    </div>
  );
}
